/**
 * ClinicianFocusPanel — what a clinician needs to ACTION today.
 *   1. New offers (respond before SLA expires)
 *   2. Accepted placements waiting on a first visit
 *   3. Provider OS draft documents to send for signature
 */
import React from 'react';
import { Clock, CalendarClock, FileText, Stethoscope } from 'lucide-react';
import { FocusCard, FocusHero } from './FocusCard';

const hoursTo = (iso) => {
  if (!iso) return null;
  try {
    const dt = new Date(iso);
    const diff = (dt.getTime() - Date.now()) / (1000 * 60 * 60);
    return Math.round(diff);
  } catch {
    return null;
  }
};

// Iter 161 · Give focus-card rows a human-readable primary label even
// when patient_initials is missing (mostly happens for legacy demo
// placements). Combines the best identifier we do have with the
// ordered_services badge so the clinician can tell rows apart.
const rowPrimaryLabel = (r) => {
  if (r.patient_initials && r.patient_initials !== '—') return r.patient_initials;
  const svc = (r.ordered_services || [])[0];
  if (svc) {
    const svcLabel = (typeof svc === 'string' ? svc : svc.name || svc.code) || 'Visit';
    return `${svcLabel} · placement ${r.id?.slice(0, 6) ?? '?'}`;
  }
  return `Placement ${r.id?.slice(0, 6) ?? '?'}`;
};

const rowServiceSubtitle = (r) => {
  const svcs = (r.ordered_services || []).slice(0, 2)
    .map((s) => (typeof s === 'string' ? s : s.name || s.code))
    .filter(Boolean);
  return svcs.join(' · ');
};

export function ClinicianFocusPanel({ focus }) {
  const blocks = focus?.blocks || [];
  const byKey = Object.fromEntries(blocks.map((b) => [b.key, b]));

  return (
    <div className="space-y-6" data-testid="clinician-focus-panel">
      <FocusHero
        title="Just the patients waiting on you."
        subtitle="Offers, first visits, and signatures — everything else is one click away."
        primaryAction={focus?.primary_action}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FocusCard
          block={byKey.offers_pending}
          testIdPrefix="clinician-focus"
          emptyHint="No offers waiting on you."
          renderRow={(r) => {
            const h = hoursTo(r.expires_at);
            const svc = rowServiceSubtitle(r);
            return (
              <>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 truncate">
                    {rowPrimaryLabel(r)}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                    <span className={`text-xs inline-flex items-center gap-1 ${
                      h !== null && h <= 1 ? 'text-rose-700' : 'text-slate-500'
                    }`}>
                      <Clock className="w-3 h-3" />
                      {h !== null ? `Expires in ${h <= 0 ? '0' : h}h` : 'No SLA set'}
                    </span>
                    {svc && (
                      <span className="text-xs text-slate-500 inline-flex items-center gap-1 truncate">
                        <Stethoscope className="w-3 h-3" /> {svc}
                      </span>
                    )}
                  </div>
                </div>
                <a href={`/clinician/care-requests`}
                    className="text-xs text-slate-600 hover:text-slate-900">
                  Respond →
                </a>
              </>
            );
          }}
        />

        <FocusCard
          block={byKey.first_visit_to_schedule}
          testIdPrefix="clinician-focus"
          emptyHint="Every accepted patient has a visit scheduled."
          renderRow={(r) => {
            const svc = rowServiceSubtitle(r);
            return (
              <>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 truncate">
                    {rowPrimaryLabel(r)}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                    <span className="text-xs text-amber-700 inline-flex items-center gap-1">
                      <CalendarClock className="w-3 h-3" />
                      Accepted, no visit yet
                    </span>
                    {svc && (
                      <span className="text-xs text-slate-500 inline-flex items-center gap-1 truncate">
                        <Stethoscope className="w-3 h-3" /> {svc}
                      </span>
                    )}
                  </div>
                </div>
                <a href={`/clinician/dashboard`}
                    className="text-xs text-slate-600 hover:text-slate-900">
                  Schedule →
                </a>
              </>
            );
          }}
        />

        <FocusCard
          block={byKey.documents_draft}
          testIdPrefix="clinician-focus"
          emptyHint="No drafts to route."
          renderRow={() => null}
        />
      </div>

      <p className="text-xs text-slate-400 inline-flex items-center gap-2">
        <FileText className="w-3 h-3" /> Open the full dashboard below for visits,
        notes, banking, and analytics.
      </p>
    </div>
  );
}
